import { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Box, Button, FormHelperText, Grid, List, ListItem, ListItemText } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { FormType, AuditorVisitDetail } from '../types';
import { getBeautifiedLocalDate } from '@/core';
import Customer from './Customer';
import WarningModal from './WarningModal';

type FormProps = {
  onSubmit: (data: Partial<FormType>) => void;
  initialState?: FormType;
  hasFormChanged?: (form: FormType) => boolean;
  onUnmount?: (form: FormType) => void;
  isFormEditable?: boolean;
  visit?: AuditorVisitDetail;
};

const Form = ({
  onSubmit,
  initialState,
  hasFormChanged,
  onUnmount,
  isFormEditable,
  visit,
}: FormProps) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [showModal, setShowModal] = useState(false);
  const form = useForm<FormType>({
    mode: 'onTouched',
    defaultValues: {
      datStart: '',
      datEnd: '',
    },
  });

  useEffect(() => {
    return () => {
      if (onUnmount) {
        onUnmount(form.getValues());
      }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    form.reset({
      datStart: initialState?.datStart
        ? new Date(initialState.datStart).toLocaleDateString('en-CA')
        : undefined,
      datEnd: initialState?.datEnd
        ? new Date(initialState.datEnd).toLocaleDateString('en-CA')
        : undefined,
    });
  }, [initialState, form]);

  const statusCheck = (status: string | undefined): boolean => {
    if (!status) return false;
    const numberMatch = status.match(/\d+/);

    if (
      (numberMatch && parseInt(numberMatch[0], 10) >= 40) ||
      (numberMatch && parseInt(numberMatch[0], 10) <= 11)
    ) {
      return true;
    }
    return false;
  };

  const baseSubmit = (data: FormType) => {
    if (hasFormChanged) {
      if (!hasFormChanged(data)) {
        enqueueSnackbar(t('Form was not changed.', { defaultValue: 'Form was not changed.' }), {
          variant: 'info',
        });
        return;
      }
    }
    if (
      visit?.planedTime &&
      dayjs(data.datStart).month() + 1 !== Number(visit?.planedTime.slice(4))
    ) {
      setShowModal(true);
      return;
    }
    onSubmit(data);
  };
  const validateDate = (val: string | undefined): true | string => {
    if (val && dayjs(val).isAfter(dayjs(visit?.certifExpireDate.date).add(1, 'hour')))
      return t(
        'Attention! The validity date of the {{uId}} certificate is {{date}}. The start date cannot be greater.',
        {
          defauldValue:
            'Attention! The validity date of the {{uId}} certificate is {{date}}. The start date cannot be greater.',
          uId: visit?.certifExpireDate.uId,
          date: dayjs(visit?.certifExpireDate.date).add(1, 'hour').format('YYYY-MM-DD'),
        }
      );
    return true;
  };
  return (
    <Box component="form" onSubmit={form.handleSubmit(baseSubmit)} noValidate>
      <WarningModal
        isOpen={showModal}
        setIsOpen={setShowModal}
        onSubmit={form.handleSubmit((data) => onSubmit(data))}
      />
      <Grid container spacing={3}>
        <Box
          sx={{ width: '100%', display: 'flex', flexDirection: 'column', paddingBottom: 0 }}
          padding={4}
        >
          {!!form.formState.errors.root && (
            <FormHelperText error>{form.formState.errors.root.message}</FormHelperText>
          )}
          <Box sx={{ width: '100%', display: 'flex' }} gap={3}>
            <Controller
              control={form.control}
              name="datStart"
              rules={{ validate: (value) => validateDate(value) }}
              render={({ field }) => (
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    {...field}
                    value={dayjs(field.value)}
                    slotProps={{
                      textField: {
                        error: !!form.formState.errors.datStart,
                        helperText: form.formState.errors.datStart?.message,
                      },
                    }}
                    sx={{ mt: '1rem', width: '100%' }}
                    label={t('Date start', { defaultValue: 'Date start' })}
                    onChange={(date) => {
                      form.setValue('datStart', date?.toISOString());
                      form.trigger('datStart');
                      form.setValue('datEnd', date?.toISOString());
                    }}
                    format="YYYY-MM-DD"
                    disabled={
                      !isFormEditable ||
                      !visit?.isCurrentAuditorTeamLeader ||
                      statusCheck(visit.status)
                    }
                  />
                </LocalizationProvider>
              )}
            />
            <Controller
              control={form.control}
              name="datEnd"
              render={({ field }) => (
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    {...field}
                    value={dayjs(field.value)}
                    sx={{ mt: '1rem', width: '100%' }}
                    label={t('Date end', { defaultValue: 'Date end' })}
                    format="YYYY-MM-DD"
                    disabled={
                      !isFormEditable ||
                      !visit?.isCurrentAuditorTeamLeader ||
                      statusCheck(visit.status)
                    }
                  />
                </LocalizationProvider>
              )}
            />
          </Box>
          {isFormEditable && visit?.isCurrentAuditorTeamLeader && !statusCheck(visit?.status) && (
            <Button variant="contained" type="submit" fullWidth sx={{ mt: '1rem' }}>
              {t('Save date', { defaultValue: 'Save date' })}
            </Button>
          )}
        </Box>
        <Grid item md>
          <List sx={{ display: 'flex', flexDirection: 'column' }}>
            <ListItem>
              <ListItemText
                primaryTypographyProps={{ fontSize: '1.25rem' }}
                secondaryTypographyProps={{ fontSize: '1rem' }}
                primary={t('Status', { defaultValue: 'Status' })}
                secondary={visit?.status || '/'}
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primaryTypographyProps={{ fontSize: '1.25rem' }}
                secondaryTypographyProps={{ fontSize: '1rem' }}
                primary={t('Note for auditors', { defaultValue: 'Note for auditors' })}
                secondary={visit?.noteForAuditors || '/'}
              />
            </ListItem>
            {visit?.isCurrentAuditorTeamLeader && (
              <ListItem>
                <ListItemText
                  primaryTypographyProps={{ fontSize: '1.25rem' }}
                  secondaryTypographyProps={{ fontSize: '1rem' }}
                  primary={t('Leader note - consumption of hours', {
                    defaultValue: 'Leader note - consumption of hours',
                  })}
                  secondary={visit?.auditorLeaderNote || '/'}
                />
              </ListItem>
            )}
            <ListItem>
              <ListItemText
                primaryTypographyProps={{ fontSize: '1.25rem' }}
                secondaryTypographyProps={{ fontSize: '1rem' }}
                primary={t('Client classifications', { defaultValue: 'Client classifications' })}
                secondary={visit?.workOrder?.clasifPack || '/'}
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primaryTypographyProps={{ fontSize: '1.25rem' }}
                secondaryTypographyProps={{ fontSize: '1rem' }}
                primary={t('Parent classifications', { defaultValue: 'Parent classifications' })}
                secondary={visit?.parentClassifications || '/'}
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primaryTypographyProps={{ fontSize: '1.25rem' }}
                secondaryTypographyProps={{ fontSize: '1rem' }}
                primary={t('Number of employess', { defaultValue: 'Number of employess' })}
                secondary={visit?.numOfEmployees || '/'}
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primaryTypographyProps={{ fontSize: '1.25rem' }}
                secondaryTypographyProps={{ fontSize: '1rem' }}
                primary={t('Proposed time', { defaultValue: 'Proposed time' })}
                secondary={visit?.proposedTime || '/'}
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primaryTypographyProps={{ fontSize: '1.25rem' }}
                secondaryTypographyProps={{ fontSize: '1rem' }}
                primary={t('Planned time', { defaultValue: 'Planned time' })}
                secondary={visit?.planedTime || '/'}
              />
            </ListItem>

            <ListItem>
              <ListItemText
                primaryTypographyProps={{ fontSize: '1.25rem' }}
                secondaryTypographyProps={{ fontSize: '1rem' }}
                primary={t('Time confirmation date', { defaultValue: 'Time confirmation date' })}
                secondary={visit?.datConf ? getBeautifiedLocalDate(visit.datConf) : '/'}
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primaryTypographyProps={{ fontSize: '1.25rem' }}
                secondaryTypographyProps={{ fontSize: '1rem' }}
                primary={t('Scope', { defaultValue: 'Scope' })}
                secondary={visit?.workOrder?.scope || '/'}
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primaryTypographyProps={{ fontSize: '1.25rem' }}
                secondaryTypographyProps={{ fontSize: '1rem' }}
                primary={t('Scope english', { defaultValue: 'Scope english' })}
                secondary={visit?.workOrder?.scopeEn || '/'}
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primaryTypographyProps={{ fontSize: '1.25rem' }}
                secondaryTypographyProps={{ fontSize: '1rem' }}
                primary={t('Scope customer language', { defaultValue: 'Scope customer language' })}
                secondary={visit?.workOrder?.scopeNative || '/'}
              />
            </ListItem>
          </List>
        </Grid>
        <Grid item md>
          <Box display="flex" flexDirection="column" alignItems="center" gap={2} padding={2}>
            <Customer />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Form;
